import type { ChangeEvent, VoidFunctionComponent } from 'react'

import {
	BodyL,
	PrimaryButton,
	FlowPage,
	Modal,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalWrapper,
	TertiaryIconButton,
} from '@monthio/design-system'
import { Close } from '@monthio/icons'
import {
	FlowContainer,
	FormHelperText,
	LoadingScreen,
	notificationService,
	RegularButtonWrapper,
	TextButton,
	TextInputField,
} from '@monthio/react-components'
import { routeCodes } from '#modules/Bank/Bank.types'
import { useFormik } from 'formik'
import i18next from 'i18next'
import Cookies from 'js-cookie'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'

import type { RootState } from '../../Redux/reducers'

import { authUrlCookieName, monthioInternalBankIdCookieName } from '../../Bank'
import {
	clearState,
	createBankLoginUrl,
} from '../../Redux/actions/consumerDataPullSessionActions/bankLoginActions'
import { storeRedirectWindowApplicantId } from '../../Redux/actions/redirectWindowActions'
import { BankLoginProcessStatus } from '../../Redux/reducers/consumerDataPullSessionReducers/bankLoginReducer'
import { BankSessionStatus } from '../../Redux/reducers/consumerDataPullSessionReducers/bankSessionReducer'
import {
	BackButtonContainer,
	SnnTextInputFieldWrapper,
} from './bankLoginStyles'
import { ProfileTypeSelector } from './ProfileTypeSelector/ProfileTypeSelector'

interface Message<T = any> {
	payload: {
		event: string
		payload: T
	}
	source: string
}

type WindowMessage<T = any> =
	| Message<T>
	| { source?: never; [key: string]: any }

const bankLoginPageValidationSchema = Yup.object({
	ssn: Yup.string()
		.required()
		.length(11, i18next.t('bankLogin-ssn-validation-error')),
})

export interface BankLoginPageProps {
	monthioInternalBankId?: number
	bankId?: string
}

export const BankLoginPage: VoidFunctionComponent<BankLoginPageProps> = ({
	monthioInternalBankId,
	bankId,
}) => {
	const { t } = useTranslation()
	const { bankLogin, bankSessions } = useSelector((state: RootState) => {
		return {
			bankLogin: state.bankLogin,
			bankSessions: state.bankSessions,
		}
	})
	const history = useHistory()
	const goBack = useCallback(() => history.replace(routeCodes.banks), [history])
	const dispatch = useDispatch()
	const [autoPopupUsed, setAutoPopupUsed] = useState(false)
	const [authUrlInUse, setAuthUrlInUse] = useState<string | null>(null)

	const params = new URLSearchParams(window.location.search)
	const demoProfile = params.get('demoProfile')

	const formik = useFormik({
		initialValues: {
			ssn: undefined,
		},
		validationSchema: bankLoginPageValidationSchema,
		onSubmit: () => {
			/* intentionally left blank (???!!!!?!?!?!!!) */
		},
	})

	const isDnb = bankId === 'NO_Dnb'
	const creatingBankSession = bankSessions.status === BankSessionStatus.CREATING

	const openPopUpHandler = () => {
		openPopUp(formik.values.ssn)
	}

	const handleSsnInputChange = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target

		if (/^\d*$/.test(value)) {
			formik.handleChange(event)
		}
	}

	useEffect(() => {
		if (
			bankLogin.authUrl == null &&
			monthioInternalBankId != null &&
			!bankLogin.creatingBankUrl
		) {
			if (isDnb) {
				formik.setFieldValue('ssn', '')
				return
			}
			dispatch(createBankLoginUrl(monthioInternalBankId))
		}
	}, [bankLogin.authUrl])

	useEffect(() => {
		return () => {
			dispatch(clearState())
		}
	}, [])

	useEffect(() => {
		if (
			bankSessions.status === BankSessionStatus.FETCH_SUCCESS &&
			autoPopupUsed
		) {
			history.replace(routeCodes.bankSessionOverviewPage)
		}
	}, [history, bankSessions])

	useEffect(() => {
		const eventHandler = (event: MessageEvent<WindowMessage>) => {
			const eventData = event.data
			const eventSource = eventData?.source
			if (eventSource != null) {
				const eventPayload = eventData.payload
				if (eventSource === 'smartcheck-flow-orchestrator-client-app') {
					switch (eventPayload.event) {
						case 'transactionClientApp/consentCodeReceived':
							history.replace(routeCodes.bankSessionOverviewPage, {
								authUrl: authUrlInUse,
								consentCode: eventPayload.payload.consentCode,
								monthioInternalBankId: bankLogin.monthioInternalBankId,
							})
							break
						case 'transactionClientApp/consentCodeReceivedFailed':
							console.error('Bank login failed', eventPayload.payload ?? event)
							notificationService.error(
								t('bankLogin-connectionFailed-snackbar'),
							)
							history.replace(routeCodes.banks)
							break
					}
				}
			}
		}
		window.addEventListener('message', eventHandler, true)
		if (bankLogin.monthioInternalBankId) {
			Cookies.set(
				monthioInternalBankIdCookieName,
				bankLogin.monthioInternalBankId.toString(),
				{
					expires: 1,
				},
			)
		}
		if (authUrlInUse) {
			Cookies.set(authUrlCookieName, authUrlInUse, {
				expires: 1,
			})
		}

		return () => {
			window.removeEventListener('message', eventHandler, true)
		}
	}, [bankLogin.monthioInternalBankId, authUrlInUse])

	const openPopUp = (ssn?: string) => {
		if (isDnb && monthioInternalBankId != null) {
			dispatch(createBankLoginUrl(monthioInternalBankId, ssn))
		}
		if (bankLogin.status !== BankLoginProcessStatus.SUCCESS_STATUS) {
			return
		}
		window.open(
			bankLogin.authUrl + '',
			'redirectWindow',
			'width=900, height=600',
		)
		dispatch(storeRedirectWindowApplicantId())
		setAuthUrlInUse(bankLogin.authUrl)

		if (bankLogin.monthioInternalBankId) {
			dispatch(createBankLoginUrl(bankLogin.monthioInternalBankId, ssn))
		}
	}

	useEffect(() => {
		const shouldOpenPopup =
			!autoPopupUsed &&
			bankLogin.authUrl &&
			!['customBank', 'demoProfile'].includes(bankLogin.authUrl)

		if (shouldOpenPopup) {
			if (isDnb) {
				openPopUp(formik.values.ssn)
			} else {
				openPopUp()
			}

			setAutoPopupUsed(true)
		}
	}, [bankLogin.authUrl])

	if (bankLogin.createBankLoginError) {
		return (
			<FlowContainer>
				<Modal isOpen>
					<ModalWrapper>
						<ModalHeader>
							<TertiaryIconButton
								ariaLabel={t('btn-close')}
								icon={<Close aria-hidden />}
								onClick={goBack}
							/>
						</ModalHeader>
						<ModalContent>
							<ModalTitle>{t('bankLogin-bankLoginError-title')}</ModalTitle>
							<BodyL>{t('bankLogin-bankLoginError-text')}</BodyL>
						</ModalContent>
						<ModalFooter>
							<PrimaryButton onClick={goBack}>{t('btn-close')}</PrimaryButton>
						</ModalFooter>
					</ModalWrapper>
				</Modal>
			</FlowContainer>
		)
	}

	//#region DNB integration
	if (isDnb) {
		return (
			<FlowPage>
				<FlowPage.Content $spacing="xl">
					<FlowPage.Header
						title={t('bankLogin-popUp-header')}
						description={t('bankLogin-popUp-text')}
					/>
					<SnnTextInputFieldWrapper>
						<TextInputField
							type="tel"
							name="ssn"
							maxLength={11}
							variant="outlined"
							pattern="\d*"
							label={t('bankLogin-ssn-label')}
							onChange={handleSsnInputChange}
							onBlur={formik.handleBlur}
							value={formik.values.ssn}
						/>
						{formik.touched.ssn && formik.errors.ssn ? (
							<FormHelperText error>
								{t('bankLogin-ssn-validation-error')}
							</FormHelperText>
						) : null}
					</SnnTextInputFieldWrapper>
				</FlowPage.Content>
				<FlowPage.Footer
					backButton={
						<FlowPage.BackButton
							ariaLabel={t('bankLogin-return-button-ariaLabel')}
							onClick={() => history.replace(routeCodes.banks)}
						/>
					}
					actions={
						<FlowPage.Cta
							onClick={openPopUpHandler}
							disabled={Object.keys(formik.errors).length > 0}
						>
							{t('bankLogin-openPopUp-button')}
						</FlowPage.Cta>
					}
				/>
			</FlowPage>
		)
	}
	//#endregion

	if (bankLogin.authUrl === 'demoProfile') {
		history.replace({
			pathname: routeCodes.bankSessionOverviewPage,
			state: {
				consentCode: demoProfile,
				monthioInternalBankId: bankLogin.monthioInternalBankId,
				authUrl: bankLogin.authUrl,
			},
		})
		return null
	}

	if (bankLogin.authUrl === 'customBank') {
		return (
			<FlowPage>
				<ProfileTypeSelector
					monthioInternalBankId={bankLogin.monthioInternalBankId}
					goBack={() => history.replace(routeCodes.banks)}
				/>
			</FlowPage>
		)
	}

	if (creatingBankSession) {
		return (
			<LoadingScreen
				title={t('bankSessionOverview-loading-text')}
				description={t('bankSessionOverview-loading-text-1')}
			/>
		)
	}

	if (bankLogin.authUrl) {
		return (
			<FlowPage>
				<FlowPage.Content>
					<FlowPage.Header
						title={t('bankLogin-popUp-header')}
						description={t('bankLogin-popUp-text')}
					/>
				</FlowPage.Content>
				<FlowPage.Footer
					backButton={
						<FlowPage.BackButton
							ariaLabel={t('bankLogin-return-button-ariaLabel')}
							onClick={() => history.replace(routeCodes.banks)}
						/>
					}
					actions={
						<FlowPage.Cta onClick={openPopUpHandler}>
							{t('bankLogin-openPopUp-button')}
						</FlowPage.Cta>
					}
				/>
			</FlowPage>
		)
	}

	if (bankLogin.creatingBankUrl) {
		return (
			<LoadingScreen
				title={t('bankLoginPage-creatingBankLogin-text')}
				description={t('bankLoginPage-creatingBankLogin-text1')}
			/>
		)
	}

	return (
		<FlowContainer>
			<BackButtonContainer>
				<RegularButtonWrapper>
					<TextButton
						onClick={() => {
							history.replace(routeCodes.banks)
						}}
					>
						{t('bankLogin-return-button')}
					</TextButton>
				</RegularButtonWrapper>
			</BackButtonContainer>
		</FlowContainer>
	)
}
